/* Proposal to Mediability – English. A digital proposal letter with a product inside: letter → one opportunity (the system in three views) → six steps → AI → Visma → delivery → price → next step. */
import { CASES } from "../cases";

const P = "/tilbod/mediability/";
const T = "/tilbod/kviteberg/team/";
const byId = Object.fromEntries(CASES.map((c) => [c.id, c]));

export const UI_EN = {
  proposal: "Proposal",
  draft: "Draft",
  pdf: "Download PDF",
  pdfBusy: "Preparing PDF …",
  skip: "Skip to content",
  to: "To",
  from: "From",
  confidential: "Confidential",
  validUntil: "Valid until",
  page: "Page",
  date: "Date",
  reference: "Reference",
  contact: "Contact",
  proposalTo: "Proposal to",
  fileWord: "Proposal",
  prototype: "Working prototype with fictional customer data. Click the tabs.",
  yes: "Yes",
  no: "—",
  contents: "Contents",
};

export const MEDIABILITY = {
  kind: "product",
  pdf: "product",
  lang: "en",
  ui: UI_EN,
  slug: "mediability",
  ref: "KV-2026-MB-01",
  status: "Tilbod",
  validDays: 30,
  brand: "Mediability",
  title: "Complex sales.\nAll in one system.",
  to: { company: "Mediability AS", person: "David Besseberg", role: "Management team", address: "Norway", fill: false },
  from: { company: "Kviteberg AS", person: "Martin Kviteberg", role: "Responsible for the delivery", email: "martin@kviteberg.no", address: "Kvinnherad, Norway", photo: P + "martin.jpg" },

  pages: ["cover", "letter", "live", "idea", "flow", "ai", "visma", "delivery", "price", "about", "next"],

  hero: {
    sub: "Mediability sells complex solutions. We propose a tailored, AI-driven system that brings requirements, solution, calculation, proposal and order basis together in one workflow – integrated with Visma Business.",
  },

  /* under the letter: from inquiry to finished proposal – the steps on the left, the customer's proposal on the right */
  showcase: {
    eyebrow: "From inquiry to proposal",
    title: "A complete proposal\nin five minutes.",
    lede: "Studio Bergen: NOK 2.3M across nine product groups. An extensive inquiry is read, structured, calculated and made ready to send in a few minutes.",
    steps: [
      { t: "0:00", title: "The inquiry arrives" },
      { t: "0:40", title: "Requirements and needs extracted" },
      { t: "1:30", title: "Products and solution assembled" },
      { t: "3:00", title: "Calculation completed and checked" },
      { t: "4:30", title: "The proposal is generated" },
      { t: "5:00", title: "Ready to send to the customer" },
    ],
    url: "proposals.mediability.no/q/studio-bergen",
    tour: "Scrolls by itself · scroll to take over",
    tourTouch: "Scrolls by itself",
  },

  live: {
    eyebrow: "Prototype",
    title: "Try the prototype yourself.",
    lede: "It runs with fictional customer data. Sign in, open Studio Bergen and go all the way from inquiry to proposal.",
    hint: "Fictional customer data",
    openFull: "Open the prototype",
    ctaSub: "Full screen · starts on the sign-in page",
    open: "Open in a new window",
    restart: "Start over",
    close: "Close",
    showControls: "Show controls",
    loading: "Loading the prototype …",
  },

  letter: {
    greeting: "Hi Lars,",
    close: "Kind regards",
    p: [
      "Thank you for the opportunity to present a proposal to Mediability.",
      "We have done our best to understand your sales process, and propose a tailored system that brings customer inquiries, requirements, solution design, calculation and proposals together in one workflow – integrated with Visma Business.",
      "AI is used where it can reduce manual work, while price, margin and approvals are still governed by Mediability.",
      "To make the proposal concrete, we have already built a working prototype with fictional data. It is meant as a starting point for developing the final solution together with you.",
    ],
  },

  idea: {
    eyebrow: "The system",
    title: "One workspace.\nAll the way to order.",
    p: ["Inbox, opportunities, solution, calculation, proposals and dashboard in one system – integrated with Visma Business. The opportunity is built once and follows the customer through the whole sale."],
    caption: "The pipeline in the prototype. Fictional customer data.",
    /* the system in three views – the mockup switches between them */
    views: [
      { key: "inbox", label: "Inbox", hint: "New inquiries", caption: "The inbox in the prototype: AI reads the e-mail, finds requirements and open questions, creates the opportunity and suggests a reply. Fictional customer data." },
      { key: "pipeline", label: "Opportunities", hint: "Active deals", caption: "The pipeline in the prototype: one opportunity moves from inquiry to an order in Visma Business. Fictional customer data." },
      { key: "opportunity", label: "Opportunity", hint: "Studio Bergen", caption: "One opportunity in the prototype: requirements, solution, calculation and quote on the same page – until the order is in Visma Business. Fictional customer data." },
      { key: "workspace", label: "AI Workspace", hint: "AI assistant", caption: "The AI Workspace in the prototype: the inquiry is pasted in, AI structures the needs, proposes a solution, calculates and writes the proposal. Fictional customer data." },
      { key: "quotes", label: "Quotes", hint: "Proposals", caption: "The quotes in the prototype: what is awaiting a decision, for how long, and what has been accepted. Fictional customer data." },
      { key: "overview", label: "Dashboard", hint: "Overview", caption: "The overview in the prototype: pipeline, what needs attention, today's meetings and suggested next moves. Fictional customer data." },
    ],
    stages: ["Inquiry", "Needs", "Solution", "Calculation", "Proposal", "Negotiation", "Order"],
  },

  proto: {
    title: "This is what it could look like.",
    p: "We have already built a working prototype to make the discussion concrete. The data is fictional, but the workflow shows the direction.",
    caption: "The opportunities in the prototype, with fictional customer data.",
    img: P + "pipeline.jpg",
    coverImg: P + "opp-overview.jpg",
  },

  flow: {
    title: null,
    head: "Six steps",
    steps: [
      ["Inquiry", "An e-mail, web form, meeting or tender is created as a new opportunity."],
      ["Needs", "Requirements, open questions and missing information are structured."],
      ["Solution", "Products, services and suppliers are assembled into the right solution."],
      ["Calculation", "Cost, sales price, discount and margin are calculated according to defined rules."],
      ["Proposal", "The approved solution becomes a digital, version-controlled proposal."],
      ["Order", "The accepted proposal is transferred to Visma Business without double entry."],
    ],
  },

  ai: {
    title: "AI that does the groundwork.",
    p: ["AI should make the workflow faster – not the system more complicated.", "Among other things, it can:"],
    list: ["read customer inquiries and tenders", "identify requirements and open questions", "structure technical information", "propose products and solutions", "draft first versions of proposals", "surface relevant history"],
    after: "People make the decisions. The system does the groundwork and keeps the overview.",
    example: {
      from: "E-mail from the customer",
      to: "AI finds 7 requirements and 2 open questions",
      img: P + "opp-requirements.jpg",
      pdfCaption: "The requirement list in the prototype, derived from one e-mail. Fictional data.",
      caption: "The AI Workspace in the prototype: from an empty workspace and one pasted e-mail to needs, solution, calculation and a sent proposal. Fictional data.",
    },
  },

  visma: {
    title: "Visma Business stays\nwhere it belongs.",
    p: [
      "We do not propose replacing Visma Business as the finance and order system.",
      "The new system becomes the working surface for the commercial process – and the place where customers, products, suppliers, solutions, calculations and proposals are built and maintained.",
      "When a solution is approved, the necessary data is transferred automatically to Visma Business for orders, invoicing and further financial processing.",
    ],
    line: ["Mediability Workspace", "Visma Business"],
    under: ["Customers · products · suppliers · requirements · solutions · prices · calculations · proposals", "Sales orders · invoicing · finance"],
    after: "The goal is that users never have to maintain the same information in several systems. Data is entered and worked on in the new solution and transferred to Visma Business when it becomes part of the financial process.",
    note: "The exact integration, and which data is synchronised in each direction, is settled at project start based on Mediability's Visma Business setup.",
  },

  delivery: {
    title: "From prototype to production\nin eight weeks.",
    p: "The prototype lets us start with what matters most: adapting the solution to Mediability, connecting the right data and integrations, testing on real sales cases – and putting the system into operation.",
    phases: [
      ["Weeks 1–2", "Understand and lock", "We go through today's sales flow, roles, approvals, product and supplier data and existing Salesforce data. At the same time we settle which data should flow on to Visma Business."],
      ["Weeks 3–4", "Build and integrate", "The system is adapted to Mediability with product catalogue, calculation, margin rules, proposals, Microsoft 365 sign-in and the first integration with Visma Business."],
      ["Weeks 5–6", "Test on real deals", "Relevant Salesforce data is migrated, and a small pilot group uses the system in real sales. Workflow, AI support and rules are fine-tuned after actual use."],
      ["Weeks 7–8", "Go live", "Final adjustments are made before training, go-live and the transition to the new system."],
    ],
    after: "Then 30 days of close follow-up after go-live.",
    includedTitle: "This is in the first delivery",
    includedItems: [
      ["Sales and customers", "Pipeline, opportunities, customers and contacts"],
      ["Needs and solution", "Requirements, clarifications and solution builder"],
      ["Products and suppliers", "Product catalogue, suppliers, cost and price"],
      ["Calculation and control", "Margin, discount and approval rules"],
      ["Proposals", "Digital proposals, PDF and version history"],
      ["AI", "Inquiries, tenders, requirements, solution and proposals"],
      ["Integrations", "Visma Business and Microsoft 365"],
      ["Start-up", "Salesforce migration, training and 30 days of follow-up"],
    ],
    outside: "Project delivery, service, a customer portal and other work processes can later be added as separate modules.",
  },

  price: {
    title: "Price and delivery",
    intro: [
      "Based on the solution outlined in this proposal, we estimate the first production version at NOK 150 000–200 000 excl. VAT.",
      "This is our best estimate based on today's understanding of Mediability's needs. Before the start we go through the solution together, settle the exact scope and give a concrete offer with final price, delivery and schedule.",
    ],
    cards: [
      { key: "dev", value: "NOK 150–200 000", label: "Estimated project price", text: "First production version, based on the proposed delivery." },
      { key: "licence", value: "NOK 15 000 / month", label: "Platform licence", text: "All users included. Hosting, maintenance, support and normal AI usage." },
      { key: "time", value: "About 8 weeks", label: "Estimated delivery", text: "From project start to the first production version." },
    ],
    stepsTitle: "From estimate to a concrete offer",
    steps: [
      ["Clarify", "We go through the prototype and prioritise what the first version should contain."],
      ["Define", "Functionality, integrations, data migration and boundaries are specified."],
      ["Offer", "Mediability receives a concrete offer with agreed scope, price and schedule before the start."],
    ],
    basisTitle: "The estimate is based on",
    basis: [
      ["Workflow", "Pipeline, customers, requirements, solution, calculation, approval and proposals."],
      ["Data and integrations", "Product and supplier data, relevant Salesforce migration, Microsoft 365 and Visma Business."],
      ["Start-up", "Pilot, training, go-live and 30 days of follow-up."],
    ],
    basisNote: "The final scope can be adjusted together before the offer is finalised.",
    licenceTitle: "Platform licence",
    licence: [
      "The platform licence is NOK 15 000 per month excl. VAT for all of Mediability, all users included.",
      "It covers hosting in the EU/EEA, backup and monitoring, maintenance, security updates, normal AI usage and support. The licence starts at go-live and has three months' notice.",
    ],
    vismaTitle: "Visma Business integration",
    vismaNote:
      "The estimate includes establishing the integration with Mediability's existing Visma Business environment, within the integration scope described after technical clarification. Any specific third-party costs, adaptations or limitations in the existing Visma setup are settled before the final offer.",
    /* PDF only */
    big: [
      ["NOK 150–200 000", "Estimated project price"],
      ["NOK 15 000 / month", "Platform licence"],
      ["About 8 weeks", "Estimated delivery"],
    ],
    termsTitle: "Terms in brief",
    terms: [
      "Mediability owns its data and can have it exported in usable formats upon termination.",
      "Kviteberg owns the platform and source code. Mediability has the right to use it for as long as the licence runs. An arrangement for source-code continuity can be agreed separately.",
      "EU/EEA hosting, role-based access, logging and Microsoft 365 sign-in are included. A data processing agreement is entered into where needed.",
      "Existing Visma Business licences and the necessary access to Visma Business are Mediability's responsibility.",
      "Work outside the agreed scope is specified and approved before it is carried out.",
    ],
  },

  about: {
    title: "Business understanding meets technical craft.",
    p: [
      "Kviteberg AS is a Norwegian consulting and software company based in Kvinnherad. We combine advisory work with our own development – so the analysis and the system that gets built come from the same hands.",
      "Behind the company are Martin, Bertine, Per Ove and Sigrid Kviteberg – a small team with a short path from decision to delivery, and a network of specialists where the assignment calls for it.",
      "We come from the family behind Adonis, business-critical software used by shipping companies worldwide since 1988.",
    ],
    checks: ["Advisory, development and operations in one delivery", "You own your data – full transparency", "Based in Kvinnherad, delivering across Norway"],
    team: [
      { name: "Martin Kviteberg", role: "Founder · Advisory and development", img: T + "martin.jpg" },
      { name: "Bertine Kviteberg", role: "Advisor", img: T + "bertine.jpg" },
      { name: "Per Ove Kviteberg", role: "Strategic partner", img: T + "per-ove.jpg" },
      { name: "Sigrid Kviteberg", role: "Advisor", img: T + "sigrid.jpg" },
    ],
    site: { label: "kviteberg.no", url: "https://kviteberg.no" },
    refsLabel: "Selected work",
    refs: [
      { id: "travelease", k: "TravelEase.AI" },
      { id: "vestmaskin", k: "Vest Maskinservice" },
      { id: "digihome", k: "DigiHome" },
    ],
    get refItems() {
      return this.refs.map((r) => ({ ...byId[r.id], ...r }));
    },
  },

  next: {
    title: "The way forward",
    p: ["If Mediability wishes to proceed, we start with a short working session with the relevant people from sales and finance.", "There we go through today's sales flow, test the prototype against real sales cases and settle data migration, the Visma Business integration and the content of the first production version."],
    agendaTitle: "The session will typically cover:",
    agenda: [
      ["30 min", "Today's process from customer inquiry to order"],
      ["30 min", "The prototype against real sales cases"],
      ["30 min", "Data, Visma Business and the first pilot"],
    ],
    after: "After that we can confirm the final scope and a plan for the start.",
    subject: "Proposal KV-2026-MB-01 – Mediability",
  },
};
